import { Bell, Search, HelpCircle, Moon, Sun, Menu as MenuIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";

function useUnreadCount() {
  return useQuery({
    queryKey: ["notification", "unread-count"],
    queryFn: async () => {
      const { data } = await api.get("/notification/unread-count");
      return (data.data ?? data) as { unread: number };
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

type Props = {
  onOpenMobileNav?: () => void;
};

export function TopBar({ onOpenMobileNav }: Props = {}) {
  const unread = useUnreadCount();
  const [dark, setDark] = useState(
    typeof window !== "undefined" && document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  return (
    <header className="h-14 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 border-b border-border bg-background">
      {onOpenMobileNav && (
        <button
          onClick={onOpenMobileNav}
          className="lg:hidden h-9 w-9 -ml-1 grid place-items-center rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0"
          aria-label="Mở menu"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      )}
      <div className="flex-1 max-w-2xl relative min-w-0">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          className="w-full h-9 pl-10 pr-3 sm:pr-20 rounded-md border border-input bg-muted/30 text-sm placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background transition-colors"
          placeholder="Tìm văn bản, nhiệm vụ..."
        />
        <kbd className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-xxs tracking-wider font-mono text-muted-foreground/80 border border-border rounded px-1.5 py-0.5 bg-background items-center">
          ⌘K
        </kbd>
      </div>
      <div className="ml-auto flex items-center gap-1 shrink-0">
        <button
          onClick={() => setDark((d) => !d)}
          className="h-9 w-9 grid place-items-center rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Toggle theme"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button
          className="hidden sm:grid h-9 w-9 place-items-center rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Help"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        <button
          className="relative h-9 w-9 grid place-items-center rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {(unread.data?.unread ?? 0) > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold grid place-items-center leading-none ring-2 ring-background">
              {unread.data!.unread > 99 ? "99+" : unread.data!.unread}
            </span>
          )}
        </button>
      </div>
    </header>
  );
}
